import { resolveEmbedApiBaseUrl } from './context-api';

export type WidgetFeedbackDraft = {
    type: 'feature_request' | 'bug' | 'improvement';
    title: string;
    /** HTML description (plain text + inline images), same as TipTap editor saves. */
    description: string;
    context: string | null;
};

export type WidgetIdentifyResponse = {
    sessionToken: string;
    visitor: {
        publicId: string;
        email: string | null;
    };
};

export type WidgetSubmitFeedbackResponse = {
    feedback: {
        publicId: string;
        title: string;
    };
};

function widgetApiBase(apiBaseUrl?: string): string {
    return resolveEmbedApiBaseUrl(apiBaseUrl);
}

function extractErrorMessage(
    body: Record<string, unknown>,
    fallback: string,
): string {
    if (typeof body.message === 'string' && body.message !== '') {
        return body.message;
    }

    const errors = body.errors;

    if (errors && typeof errors === 'object') {
        for (const value of Object.values(errors)) {
            if (Array.isArray(value) && typeof value[0] === 'string') {
                return value[0];
            }
        }
    }

    return fallback;
}

export async function identifyWidgetVisitorByEmail(
    projectId: string,
    email: string,
    apiBaseUrl?: string,
): Promise<WidgetIdentifyResponse> {
    const base = widgetApiBase(apiBaseUrl);
    const url = new URL(
        `/api/widget/v1/projects/${encodeURIComponent(projectId)}/session/identify`,
        base || undefined,
    );

    const response = await fetch(url.toString(), {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
        },
        credentials: 'omit',
        body: JSON.stringify({ email }),
    });

    if (!response.ok) {
        const body = (await response.json().catch(() => ({}))) as Record<
            string,
            unknown
        >;

        throw new Error(
            extractErrorMessage(body, `Identify failed (${response.status})`),
        );
    }

    return (await response.json()) as WidgetIdentifyResponse;
}

export async function submitWidgetFeedback(
    projectId: string,
    sessionToken: string,
    draft: WidgetFeedbackDraft,
    apiBaseUrl?: string,
): Promise<WidgetSubmitFeedbackResponse> {
    const base = widgetApiBase(apiBaseUrl);
    const url = new URL(
        `/api/widget/v1/projects/${encodeURIComponent(projectId)}/feedback`,
        base || undefined,
    );

    const response = await fetch(url.toString(), {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${sessionToken}`,
        },
        credentials: 'omit',
        body: JSON.stringify({
            title: draft.title,
            type: draft.type,
            description: draft.description || null,
            context: draft.context,
        }),
    });

    if (!response.ok) {
        const body = (await response.json().catch(() => ({}))) as Record<
            string,
            unknown
        >;

        throw new Error(
            extractErrorMessage(body, `Submit failed (${response.status})`),
        );
    }

    return (await response.json()) as WidgetSubmitFeedbackResponse;
}
